import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Table, TableBody, TableCell, TableHead, TableRow,
    Typography, Paper, Stack, Grid, MenuItem, IconButton, Card,
    TableContainer, Checkbox, ListItemText, OutlinedInput, Select,
    FormControl, InputLabel, Chip, CircularProgress, Avatar
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
    setDoc, doc, getDocs, collection, updateDoc, deleteDoc,
    query, where, getDoc, orderBy
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { toast } from 'react-toastify';
import {
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Class as ClassIcon
} from '@mui/icons-material';

const Teachersclass = () => {
    const uid = auth.currentUser?.uid;
    const [schoolId, setSchoolId] = useState(null);
    const [activeYear, setActiveYear] = useState('');
    const [academicYears, setAcademicYears] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [classes, setClasses] = useState([]);
    const [allSubjects, setAllSubjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        teacherId: '', classId: '', isClassTeacher: 'No', subjects: [], academicYear: ''
    });

    useEffect(() => {
        const initSystem = async () => {
            if (!uid) return;
            try {
                const userSnap = await getDoc(doc(db, 'users', uid));
                if (userSnap.exists()) {
                    const sId = userSnap.data().schoolId;
                    setSchoolId(sId);

                    // Parallel fetching for performance
                    const [aySnap, tSnap, cSnap, sSnap] = await Promise.all([
                        getDocs(query(collection(db, 'academicyears'), where('schoolId', '==', sId), orderBy('name', 'desc'))),
                        getDocs(query(collection(db, 'users'), where('schoolId', '==', sId), where('role', '==', 'Teacher'))),
                        getDocs(query(collection(db, 'courses'), where('schoolId', '==', sId), orderBy('sequence', 'asc'))),
                        getDocs(query(collection(db, 'subjects'), where('schoolId', '==', sId)))
                    ]);

                    const yearsData = aySnap.docs.map(d => d.data());
                    const yearNames = yearsData.map(y => y.name);
                    setAcademicYears(yearNames);

                    const currentYear = yearsData.find(y => y.status === 'Active')?.name || yearNames[0] || '';
                    setActiveYear(currentYear);
                    setFormData(prev => ({ ...prev, academicYear: currentYear }));

                    setTeachers(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                    // Crucial: We store the sorted classes here
                    setClasses(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                    setAllSubjects(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                }
            } catch (err) { toast.error("Initialization failed"); }
        };
        initSystem();
    }, [uid]);

    useEffect(() => {
        if (schoolId && activeYear) fetchAllocations();
    }, [schoolId, activeYear]);

    const fetchAllocations = async () => {
        setFetching(true);
        try {
            const q = query(collection(db, 'teacher_allocations'), where('schoolId', '==', schoolId), where('academicYear', '==', activeYear));
            const snap = await getDocs(q);
            setAllocations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) { toast.error("Error loading allocations"); }
        setFetching(false);
    };

    /** * Logic: Iterate through the 'classes' array (which is already ordered by sequence) 
     * and pick matching allocations for each class.
     */
    const sortedGroupedData = useMemo(() => {
        const result = [];
        
        classes.forEach(cls => {
            const classAllocations = allocations.filter(a => {
                const teacher = teachers.find(t => t.id === a.teacherId);
                const tName = teacher?.name || '';
                const matchesSearch = (tName + a.classId).toLowerCase().includes(searchTerm.toLowerCase());
                return a.classId === cls.name && matchesSearch;
            });

            if (classAllocations.length > 0) {
                result.push({
                    className: cls.name,
                    teachers: classAllocations
                });
            }
        });

        return result;
    }, [allocations, searchTerm, teachers, classes]);

    const filteredSubjectsForClass = useMemo(() => {
        return allSubjects.filter(sub => sub.course_code === formData.classId);
    }, [formData.classId, allSubjects]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubjectChange = (event) => {
        const { value } = event.target;
        setFormData(prev => ({ ...prev, subjects: typeof value === 'string' ? value.split(',') : value }));
    };

    const handleSubmit = async () => {
        if (!formData.teacherId || !formData.classId) return toast.error("Fill required fields");
        setLoading(true);
        try {
            const payload = { ...formData, schoolId, updatedAt: new Date().toISOString() };
            if (editMode) {
                await updateDoc(doc(db, "teacher_allocations", editId), payload);
            } else {
                const newId = uuidv4();
                await setDoc(doc(db, "teacher_allocations", newId), { ...payload, id: newId, createdAt: new Date().toISOString() });
            }
            setOpen(false); resetForm(); fetchAllocations(); toast.success("Saved successfully");
        } catch (err) { toast.error(err.message); }
        setLoading(false);
    };

    const resetForm = () => {
        setFormData({ teacherId: '', classId: '', isClassTeacher: 'No', subjects: [], academicYear: activeYear });
        setEditMode(false); setEditId(null);
    };

    return (
        <Box p={1} sx={{ bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: 'primary.main' }}>Faculty Allocation</Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>Academic Session: <b>{activeYear}</b></Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <TextField select size="small" value={activeYear} onChange={(e) => setActiveYear(e.target.value)} sx={{ width: 140, bgcolor: 'white' }}>
                        {academicYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </TextField>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ fontWeight: 600 }}>Assign</Button>
                </Stack>
            </Stack>

            <TextField
                size='small' fullWidth placeholder="Search by teacher or class name..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 3, bgcolor: 'white' }}
                InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} /> }}
            />

            {fetching ? (
                <Box textAlign="center" py={10}><CircularProgress size={40} /></Box>
            ) : (
                <Grid className="row" spacing={3}>
                    {sortedGroupedData.map((group) => (
                        <Grid className="col-sm-12 mb-2" key={group.className}>
                            <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                <Box sx={{ p: 1.5, bgcolor: '#f8f9fa', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ClassIcon color="primary" sx={{ fontSize: 20 }} />
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 800 }}>{group.className}</Typography>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#fafafa' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, width: '30%' }}>Faculty</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '20%' }}>Designation</TableCell>
                                                <TableCell sx={{ fontWeight: 700, width: '35%' }}>Subjects</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, width: '15%' }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {group.teachers.map((row) => {
                                                const teacher = teachers.find(t => t.id === row.teacherId);
                                                return (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                <Avatar sx={{ width: 30, height: 30, fontSize: '0.8rem', bgcolor: 'secondary.light' }}>{teacher?.name?.charAt(0)}</Avatar>
                                                                <Typography sx={{ fontSize: '0.9rem' }}>{teacher?.name || 'N/A'}</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.isClassTeacher === 'Yes' ?
                                                                <Chip label="Class Teacher" size="small" color="success" sx={{ fontSize: '0.75rem', fontWeight: 700 }} />
                                                                : <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Subject Faculty</Typography>
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                                {row.subjects?.map(s => (
                                                                    <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                                                                ))}
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" onClick={() => { setEditMode(true); setEditId(row.id); setFormData(row); setOpen(true); }}><EditIcon fontSize="small" color="primary" /></IconButton>
                                                            <IconButton size="small" onClick={async () => { if (window.confirm('Delete this allocation?')) { await deleteDoc(doc(db, 'teacher_allocations', row.id)); fetchAllocations(); } }}><DeleteIcon fontSize="small" color="error" /></IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Assignment Dialog */}
            <Dialog open={open} onClose={() => { setOpen(false); resetForm(); }} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>{editMode ? 'Edit' : 'New'} Faculty Assignment</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} sx={{ pt: 1 }}>
                        <Grid className="col-sm-12 mb-3" sm={6}>
                            <TextField select size="small" label="Class" fullWidth name="classId" value={formData.classId} onChange={handleChange}>
                                {classes.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid className="col-sm-12 mb-3" sm={6}>
                            <TextField select size="small" label="Is Class Teacher?" fullWidth name="isClassTeacher" value={formData.isClassTeacher} onChange={handleChange}>
                                <MenuItem value="No">No</MenuItem>
                                <MenuItem value="Yes">Yes</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid className="col-sm-12 mb-3">
                            <TextField select size="small" label="Select Teacher" fullWidth name="teacherId" value={formData.teacherId} onChange={handleChange}>
                                {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid className="col-sm-12 mb-3">
                            <FormControl fullWidth size="small" disabled={!formData.classId}>
                                <InputLabel>Assign Subjects</InputLabel>
                                <Select multiple value={formData.subjects} onChange={handleSubjectChange} input={<OutlinedInput label="Assign Subjects" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((val) => <Chip key={val} label={val} size="small" />)}
                                        </Box>
                                    )}>
                                    {filteredSubjectsForClass.map((sub) => (
                                        <MenuItem key={sub.id} value={sub.sub_name}>
                                            <Checkbox checked={formData.subjects.indexOf(sub.sub_name) > -1} size="small" />
                                            <ListItemText primary={sub.sub_name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : 'Confirm'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Teachersclass;